// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import { Eye, EyeOff, Pin, PinOff, Activity } from 'lucide-react';
import { CoinHeaderCell, StandardHeaderCell, getTableHeaderCellClasses, getStickyPositionStyle } from './TableHeaderCells';
import { ColumnTooltip } from './ColumnTooltip';
import CoinLogoImage from './CoinLogoImage';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface PriceComparison {
  symbol: string;
  name: string;
  logo_url?: string;
  current_price: number;
  price_change_pct: number;
  eth_price_change_pct: number;
  outperformance: number;
  correlation_with_eth: number;
  volatility: number;
  market_cap: number;
  volume_24h: number;
}

interface PriceComparisonData {
  summary: {
    total_analyzed: number;
    outperformers: number;
    underperformers: number;
    avg_outperformance: number;
    eth_performance: number;
    timeframe: string;
    insight: string;
  };
  comparisons: PriceComparison[];
  timestamp: string;
}

interface PriceComparisonETHProps {
  isOpen?: boolean;
  onToggle?: () => void;
}

const PriceComparisonETH: React.FC<PriceComparisonETHProps> = ({ isOpen: isOpenProp, onToggle: onToggleProp }) => {
  const { isDark } = useTheme();
  const [internalIsOpen, setInternalIsOpen] = useState(true);

  // Use internal state if no external control is provided
  const isOpen = isOpenProp !== undefined ? isOpenProp : internalIsOpen;
  const handleToggle = onToggleProp || (() => setInternalIsOpen(!internalIsOpen));

  // Store data for all timeframes separately
  const [dataByTimeframe, setDataByTimeframe] = useState<{
    '1Y': PriceComparisonData | null;
    '1M': PriceComparisonData | null;
    '1W': PriceComparisonData | null;
  }>({
    '1Y': null,
    '1M': null,
    '1W': null,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTimeframe, setActiveTimeframe] = useState<'1Y' | '1M' | '1W'>('1Y');

  // Get current data based on active timeframe
  const data = dataByTimeframe[activeTimeframe];

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState<{[key: string]: boolean}>(() => {
    const saved = localStorage.getItem('priceCompEthColumns');
    return saved ? JSON.parse(saved) : {
      rank: true,
      coin: true,
      priceChange: true,
      ethChange: true,
      outperformance: true,
      correlation: true,
      volatility: true,
      marketCap: true,
    };
  });

  // Pinned columns state
  const [pinnedColumns, setPinnedColumns] = useState<string[]>(() => {
    const saved = localStorage.getItem('priceCompEthPinnedColumns');
    return saved ? JSON.parse(saved) : [];
  });
  const tableScrollRef = useRef<HTMLDivElement>(null);

  // Header cell refs for width measurement
  const headerRefs = useRef<Record<string, HTMLTableCellElement | null>>({});

  // Column widths state for sticky positioning
  const [colWidths, setColWidths] = useState<Record<string, number>>({});

  // Define natural column order for consistent pinning
  const PIN_ORDER = [
    'priceChange',
    'ethChange',
    'outperformance',
    'correlation',
    'volatility',
    'marketCap',
  ];

  // Helper function to sort pinned columns according to PIN_ORDER
  const sortPinnedColumns = (columns: string[]): string[] => {
    return PIN_ORDER.filter(col => columns.includes(col));
  };

  // Toast notification for pin limit
  const [showPinLimitToast, setShowPinLimitToast] = useState(false);

  const toggleColumn = (columnKey: string) => {
    const newVisibleColumns = {
      ...visibleColumns,
      [columnKey]: !visibleColumns[columnKey]
    };
    setVisibleColumns(newVisibleColumns);
    localStorage.setItem('priceCompEthColumns', JSON.stringify(newVisibleColumns));
  };

  const togglePin = (columnKey: string) => {
    const isPinned = pinnedColumns.includes(columnKey);
    if (isPinned) {
      // Unpin column
      const filteredColumns = pinnedColumns.filter(col => col !== columnKey);
      const newPinnedColumns = sortPinnedColumns(filteredColumns);
      setPinnedColumns(newPinnedColumns);
      localStorage.setItem('priceCompEthPinnedColumns', JSON.stringify(newPinnedColumns));
    } else {
      // Check limit
      if (pinnedColumns.length >= 6) {
        setShowPinLimitToast(true);
        setTimeout(() => setShowPinLimitToast(false), 3000);
        return;
      }
      // Pin column and sort according to PIN_ORDER
      const unsortedColumns = [...pinnedColumns, columnKey];
      const newPinnedColumns = sortPinnedColumns(unsortedColumns);
      setPinnedColumns(newPinnedColumns);
      localStorage.setItem('priceCompEthPinnedColumns', JSON.stringify(newPinnedColumns));
    }
  };

  // Measure column widths from header refs
  const measureColumnWidths = () => {
    const keys = ['rank', 'coin', 'priceChange', 'ethChange', 'outperformance', 'correlation', 'volatility', 'marketCap'] as const;
    const next: Record<string, number> = {};
    keys.forEach(k => {
      const el = headerRefs.current[k];
      if (el) {
        next[k] = el.getBoundingClientRect().width;
      }
    });
    setColWidths(next);
  };

  const getStickyLeftPosition = (columnKey: string): number => {
    // Ensure we work with the properly ordered pinned columns
    const orderedPinnedColumns = sortPinnedColumns(pinnedColumns);
    const columnIndex = orderedPinnedColumns.indexOf(columnKey);
    if (columnIndex === -1) return 0; // Not pinned

    // Start after the rank and coin columns (which are always sticky)
    let leftPosition = (colWidths.rank || 0) + (colWidths.coin || 0);

    // Add widths of pinned columns that come before this one in PIN_ORDER
    for (let i = 0; i < columnIndex; i++) {
      const prevColumn = orderedPinnedColumns[i];
      leftPosition += colWidths[prevColumn] || 0;
    }

    return leftPosition;
  };


  // Mock data for fallback when API fails
  const mockData: PriceComparisonData = {
    summary: {
      total_analyzed: 50,
      outperformers: 18,
      underperformers: 32,
      avg_outperformance: 12.3,
      eth_performance: 45.2,
      timeframe: "1Y",
      insight: "Ethereum outperformed 36% of analyzed altcoins this year with 45.2% gains, showing strong performance in the DeFi and smart contract space."
    },
    comparisons: [
      {
        symbol: "SOL",
        name: "Solana",
        current_price: 98.45,
        price_change_pct: 125.6,
        eth_price_change_pct: 45.2,
        outperformance: 80.4,
        correlation_with_eth: 0.62,
        volatility: 12.8,
        market_cap: 45000000000,
        volume_24h: 2800000000
      },
      {
        symbol: "BNB",
        name: "BNB",
        current_price: 285.67,
        price_change_pct: 12.8,
        eth_price_change_pct: 45.2,
        outperformance: -32.4,
        correlation_with_eth: 0.68,
        volatility: 6.2,
        market_cap: 43000000000,
        volume_24h: 850000000
      },
      {
        symbol: "MATIC",
        name: "Polygon",
        current_price: 0.387,
        price_change_pct: 38.9,
        eth_price_change_pct: 45.2,
        outperformance: -6.3,
        correlation_with_eth: 0.81,
        volatility: 9.5,
        market_cap: 9200000000,
        volume_24h: 298000000
      },
      {
        symbol: "ADA",
        name: "Cardano",
        current_price: 0.356,
        price_change_pct: -15.3,
        eth_price_change_pct: 45.2,
        outperformance: -60.5,
        correlation_with_eth: 0.71,
        volatility: 9.1,
        market_cap: 12800000000,
        volume_24h: 267000000
      },
      {
        symbol: "LINK",
        name: "Chainlink",
        current_price: 11.23,
        price_change_pct: 28.9,
        eth_price_change_pct: 45.2,
        outperformance: -16.3,
        correlation_with_eth: 0.78,
        volatility: 8.9,
        market_cap: 7200000000,
        volume_24h: 425000000
      },
      {
        symbol: "UNI",
        name: "Uniswap",
        current_price: 7.89,
        price_change_pct: 52.1,
        eth_price_change_pct: 45.2,
        outperformance: 6.9,
        correlation_with_eth: 0.84,
        volatility: 11.2,
        market_cap: 6800000000,
        volume_24h: 156000000
      }
    ],
    timestamp: new Date().toISOString()
  };

  const fetchPriceComparisons = async (timeframe: '1Y' | '1M' | '1W', showLoadingSpinner: boolean = true) => {
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    if (!token) {
      setDataByTimeframe(prev => ({ ...prev, [timeframe]: mockData }));
      return;
    }

    if (showLoadingSpinner) {
      setLoading(true);
    }
    setError(null);

    try {
      const response = await fetch(`${API_BASE}/api/price-comparison-eth?timeframe=${timeframe}&limit=100`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setDataByTimeframe(prev => ({ ...prev, [timeframe]: result }));
    } catch (err: any) {
      console.error('Error fetching ETH price comparison:', err);
      console.warn('API failed, using mock data fallback');
      setDataByTimeframe(prev => ({ ...prev, [timeframe]: mockData }));
      setError(null); // Clear error since we're showing mock data
    } finally {
      if (showLoadingSpinner) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    // When table opens, pre-fetch ALL timeframes in parallel
    if (isOpen && !dataByTimeframe['1Y'] && !dataByTimeframe['1M'] && !dataByTimeframe['1W']) {
      // Show loading spinner only for the active timeframe
      fetchPriceComparisons('1Y', activeTimeframe === '1Y');
      fetchPriceComparisons('1M', activeTimeframe === '1M');
      fetchPriceComparisons('1W', activeTimeframe === '1W');
    }
  }, [isOpen]);

  // Add horizontal scroll on wheel
  useEffect(() => {
    const scrollContainer = tableScrollRef.current;
    if (!scrollContainer) return;

    const handleWheel = (e: WheelEvent) => {
      // Check if table has horizontal overflow
      const hasHorizontalScroll = scrollContainer.scrollWidth > scrollContainer.clientWidth;

      if (hasHorizontalScroll) {
        e.preventDefault();
        e.stopPropagation();

        // Convert vertical scroll to horizontal scroll
        const scrollAmount = e.deltaY;
        scrollContainer.scrollLeft += scrollAmount;
      }
    };

    scrollContainer.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      scrollContainer.removeEventListener('wheel', handleWheel);
    };
  }, [data]); // Re-run when data changes

  // Measure column widths on mount and when dependencies change
  useEffect(() => {
    measureColumnWidths();
    const ro = new ResizeObserver(() => measureColumnWidths());
    if (tableScrollRef.current) {
      ro.observe(tableScrollRef.current);
    }
    const handleResize = () => measureColumnWidths();
    window.addEventListener('resize', handleResize);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', handleResize);
    };
  }, [visibleColumns, pinnedColumns, isDark]);

  const formatPercentage = (value: number) => {
    const color = value >= 0 ? (isDark ? 'text-emerald-400' : 'text-green-600') : (isDark ? 'text-red-400' : 'text-red-600');
    const sign = value >= 0 ? '+' : '';
    return (
      <span className={color}>{sign}{value.toFixed(2)}%</span>
    );
  };

  const formatNumber = (num: number) => {
    if (num >= 1e9) return `$${(num / 1e9).toFixed(2)}B`;
    if (num >= 1e6) return `$${(num / 1e6).toFixed(2)}M`;
    if (num >= 1e3) return `$${(num / 1e3).toFixed(2)}K`;
    return `$${num.toFixed(2)}`;
  };

  const getOutperformanceBadge = (outperformance: number) => {
    if (outperformance > 20) return isDark ? 'bg-emerald-900/50 text-emerald-400 border-emerald-400/30' : 'bg-green-100 text-green-800 border-green-200';
    if (outperformance > 0) return isDark ? 'bg-blue-900/50 text-blue-400 border-blue-400/30' : 'bg-blue-100 text-blue-800 border-blue-200';
    if (outperformance > -20) return isDark ? 'bg-orange-900/50 text-orange-400 border-orange-400/30' : 'bg-orange-100 text-orange-800 border-orange-200';
    return isDark ? 'bg-red-900/50 text-red-400 border-red-400/30' : 'bg-red-100 text-red-800 border-red-200';
  };

  const timeframeLabels = {
    '1Y': '1 Year',
    '1M': '1 Month',
    '1W': '1 Week'
  };

  const columnLabels = {
    rank: 'Rank',
    coin: 'Coin',
    priceChange: 'Price Change',
    ethChange: 'ETH Change',
    outperformance: 'Outperformance',
    correlation: 'Correlation',
    volatility: 'Volatility',
    marketCap: 'Market Cap',
  };

  const columnAbbreviations = {
    rank: 'Rank',
    coin: 'Coin',
    priceChange: 'Price',
    ethChange: 'ETH',
    outperformance: 'Outperf',
    correlation: 'Corr',
    volatility: 'Vol',
    marketCap: 'MCap',
  };

  return (
    <div className={`rounded-2xl shadow-sm border mb-6 transition-all duration-200 ${
      isDark
        ? 'bg-white/[0.03] border-white/10'
        : 'bg-white border-gray-200'
    }`}>
      {/* Header */}
      <div
        className={`px-6 py-4 border-b cursor-pointer transition-colors duration-200 ${
          isDark
            ? 'border-white/10 hover:bg-white/5'
            : 'border-gray-200 hover:bg-gray-50'
        }`}
        onClick={handleToggle}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className={`p-2 rounded-lg ${isDark ? 'bg-emerald-500/10' : 'bg-emerald-50'}`}>
              <Activity className={`${isDark ? 'text-emerald-400' : 'text-emerald-600'}`} size={24} />
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                Price Comparison vs ETH
              </h3>
              <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                How top coins performed compared to Ethereum across different timeframes
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <div className="group relative">
              <span className={`text-lg cursor-help ${isDark ? 'text-gray-400 hover:text-gray-300' : 'text-gray-400 hover:text-gray-600'}`}>ℹ️</span>
              <div className={`absolute bottom-full right-0 mb-2 w-80 p-3 rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none z-10 ${
                isDark ? 'bg-gray-800 text-white' : 'bg-gray-900 text-white'
              }`}>
                <div className="font-medium mb-1">Price Comparison vs Ethereum:</div>
                <div className="text-xs space-y-1">
                  <div>• <strong>Price Change:</strong> Percentage change in USD price over selected timeframe</div>
                  <div>• <strong>ETH Change:</strong> Ethereum's percentage change over the same period</div>
                  <div>• <strong>Outperformance:</strong> How much the coin outperformed (or underperformed) ETH</div>
                  <div>• <strong>Correlation:</strong> Statistical correlation with Ethereum (0-1, higher = more correlated)</div>
                  <div>• <strong>Volatility:</strong> Price volatility measured as standard deviation</div>
                </div>
              </div>
            </div>
            <svg
              className={`w-5 h-5 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''} ${isDark ? 'text-gray-400' : 'text-gray-400'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Expandable Content */}
      <div className={`transition-all duration-300 ease-in-out ${
        isOpen ? 'max-h-none opacity-100' : 'max-h-0 opacity-0 overflow-hidden'
      }`}>
        {isOpen && (
          <div>
            {/* Timeframe Tabs */}
            <div className="px-6 pt-6">
              <div className={`flex space-x-1 mb-6 p-1 rounded-lg w-fit ${isDark ? 'bg-white/[0.05]' : 'bg-gray-100'}`}>
              {Object.entries(timeframeLabels).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setActiveTimeframe(key as '1Y' | '1M' | '1W')}
                  className={`px-4 py-2 text-sm font-medium rounded-md transition-colors duration-200 ${
                    activeTimeframe === key
                      ? (isDark ? 'bg-white/[0.1] text-emerald-400 shadow-sm' : 'bg-white text-blue-600 shadow-sm')
                      : (isDark ? 'text-gray-400 hover:text-gray-200' : 'text-gray-600 hover:text-gray-900')
                  }`}
                >
                  {label}
                </button>
              ))}
              </div>
            </div>

            {/* Summary Insight */}
            {data?.summary && (
              <div className="px-6">
              <div className={`rounded-lg p-4 mb-6 border ${isDark ? 'bg-blue-900/30 border-blue-400/30' : 'bg-blue-50 border-blue-200'}`}>
                <div className="flex items-start space-x-3">
                  <span className={`text-lg mt-0.5 flex-shrink-0 ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>ℹ️</span>
                  <div>
                    <p className={`font-medium ${isDark ? 'text-blue-300' : 'text-blue-800'}`}>{data.summary.insight}</p>
                    <div className={`mt-2 flex flex-wrap gap-4 text-sm ${isDark ? 'text-blue-400' : 'text-blue-700'}`}>
                      <span>Total Analyzed: <strong>{data.summary.total_analyzed}</strong></span>
                      <span>Outperformers: <strong>{data.summary.outperformers}</strong></span>
                      <span>ETH Performance: <strong className={data.summary.eth_performance >= 0 ? (isDark ? 'text-emerald-400' : 'text-green-700') : (isDark ? 'text-red-400' : 'text-red-700')}>{data.summary.eth_performance >= 0 ? '+' : ''}{data.summary.eth_performance.toFixed(2)}%</strong></span>
                    </div>
                  </div>
                </div>
              </div>
              </div>
            )}

            {/* Loading State */}
            {loading && (
              <div className="px-6">
              <div className="flex items-center justify-center py-12">
                <div className={`animate-spin rounded-full h-8 w-8 border-b-2 ${isDark ? 'border-emerald-400' : 'border-blue-600'}`}></div>
                <span className={`ml-3 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Loading price comparison data...</span>
              </div>
              </div>
            )}

            {/* Error State */}
            {error && (
              <div className="px-6">
              <div className={`rounded-lg p-4 border ${isDark ? 'bg-red-900/30 border-red-400/30' : 'bg-red-50 border-red-200'}`}>
                <div className="flex items-center space-x-2">
                  <span className={isDark ? 'text-red-400' : 'text-red-600'}>❌</span>
                  <span className={`font-medium ${isDark ? 'text-red-300' : 'text-red-800'}`}>Error loading data:</span>
                  <span className={isDark ? 'text-red-400' : 'text-red-700'}>{error}</span>
                </div>
              </div>
              </div>
            )}

            {/* Data Table */}
            {data?.comparisons && data.comparisons.length > 0 && (
              <div
                ref={tableScrollRef}
                className={`overflow-x-auto custom-scrollbar ${!isDark ? 'light-scrollbar' : ''} relative`}
                title="Use mouse wheel to scroll horizontally"
                style={{
                  scrollbarWidth: 'thin',
                  scrollbarColor: isDark ? '#4b5563 #1f2937' : '#d1d5db #f3f4f6'
                }}
              >
                <table className="w-full">
                  <thead className={isDark ? 'border-b border-white/10 text-xs uppercase tracking-wide' : 'bg-gray-50 border-b border-gray-200 text-xs uppercase tracking-wide'}>
                    <tr>
                      <th
                        ref={(el) => (headerRefs.current.rank = el)}
                        className={getTableHeaderCellClasses(visibleColumns.rank, false, isDark)}
                        style={{
                          position: 'sticky',
                          left: 0,
                          top: 0,
                          zIndex: 2,
                          background: isDark ? '#09090b' : '#ffffff',
                          minWidth: '80px',
                          width: '80px'
                        }}
                      >
                        <CoinHeaderCell title="Rank" />
                      </th>
                      <th
                        ref={(el) => (headerRefs.current.coin = el)}
                        className={getTableHeaderCellClasses(visibleColumns.coin, false, isDark)}
                        style={{
                          width: visibleColumns.coin ? '180px' : '32px',
                          minWidth: visibleColumns.coin ? '180px' : '32px',
                          position: 'sticky',
                          left: '80px',
                          top: 0,
                          zIndex: 2,
                          background: isDark ? '#09090b' : '#ffffff'
                        }}
                      >
                        {visibleColumns.coin ? (
                          <CoinHeaderCell title="Coin" />
                        ) : (
                          <StandardHeaderCell
                            colKey="coin"
                            label="Coin"
                            abbrev="C"
                            sortableIcon={false}
                            visible={visibleColumns.coin}
                            pinned={false}
                            onToggleColumn={toggleColumn}
                            onTogglePin={togglePin}
                          />
                        )}
                      </th>
                      {Object.entries(columnLabels).filter(([key]) => key !== 'coin' && key !== 'rank').map(([key, label]) => (
                        <th
                          key={key}
                          ref={(el) => (headerRefs.current[key] = el)}
                          className={getTableHeaderCellClasses(visibleColumns[key], pinnedColumns.includes(key), isDark)}
                          style={getStickyPositionStyle(pinnedColumns.includes(key), getStickyLeftPosition(key), isDark)}
                        >
                          <StandardHeaderCell
                            colKey={key}
                            label={label}
                            abbrev={columnAbbreviations[key]}
                            visible={visibleColumns[key]}
                            pinned={pinnedColumns.includes(key)}
                            onToggleColumn={toggleColumn}
                            onTogglePin={togglePin}
                          />
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {data.comparisons.map((comparison, index) => (
                      <tr key={comparison.symbol} className={`${isDark ? 'hover:bg-white/[0.05] border-b border-white/[0.05]' : 'hover:bg-gray-50 border-b border-gray-200'}`}>
                        <td
                          className={`px-4 py-4 whitespace-nowrap text-sm font-medium sticky left-0 z-10 ${isDark ? 'text-white' : 'text-gray-900'}`}
                          style={{
                            position: 'sticky',
                            left: 0,
                            top: 0,
                            zIndex: 2,
                            background: isDark ? '#09090b' : '#ffffff',
                            width: '80px',
                            minWidth: '80px'
                          }}
                        >
                          #{index + 1}
                        </td>
                        <td
                          className={`px-3 py-2 transition-all duration-300 ${visibleColumns.coin ? '' : 'w-8 max-w-8'}`}
                          style={{
                            width: visibleColumns.coin ? '180px' : '32px',
                            minWidth: visibleColumns.coin ? '180px' : '32px',
                            position: 'sticky',
                            left: '80px',
                            top: 0,
                            zIndex: 2,
                            background: isDark ? '#09090b' : '#ffffff'
                          }}
                        >
                          {visibleColumns.coin ? (
                            <div className="flex items-center gap-2">
                              <CoinLogoImage
                                src={comparison.logo_url || ''}
                                alt={comparison.symbol}
                                size="md"
                                className="mr-3"
                              />
                              <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {comparison.name && comparison.name !== comparison.symbol ? `${comparison.name} (${comparison.symbol})` : comparison.symbol}
                              </div>
                            </div>
                          ) : (
                            <ColumnTooltip
                              content={
                                <div className="flex items-center gap-2">
                                  <CoinLogoImage
                                    src={comparison.logo_url || ''}
                                    alt={comparison.symbol}
                                    size="md"
                                    className="mr-3"
                                  />
                                  <div className={`text-sm font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                    {comparison.name && comparison.name !== comparison.symbol ? `${comparison.name} (${comparison.symbol})` : comparison.symbol}
                                  </div>
                                </div>
                              }
                              onColumnClick={() => toggleColumn('coin')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-center transition-all duration-300 ${
                            visibleColumns.priceChange ? '' : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('priceChange') ? 'sticky z-10' : ''}`}
                          style={{
                            ...(pinnedColumns.includes('priceChange') ? { left: `${getStickyLeftPosition('priceChange')}px`, background: isDark ? '#09090b' : '#ffffff' } : {})
                          }}
                        >
                          {visibleColumns.priceChange ? (
                            <span className="text-sm">{formatPercentage(comparison.price_change_pct)}</span>
                          ) : (
                            <ColumnTooltip
                              content={<span className="text-sm">{formatPercentage(comparison.price_change_pct)}</span>}
                              onColumnClick={() => toggleColumn('priceChange')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-center transition-all duration-300 ${
                            visibleColumns.ethChange ? '' : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('ethChange') ? 'sticky z-10' : ''}`}
                          style={{
                            ...(pinnedColumns.includes('ethChange') ? { left: `${getStickyLeftPosition('ethChange')}px`, background: isDark ? '#09090b' : '#ffffff' } : {})
                          }}
                        >
                          {visibleColumns.ethChange ? (
                            <span className="text-sm">{formatPercentage(comparison.eth_price_change_pct)}</span>
                          ) : (
                            <ColumnTooltip
                              content={<span className="text-sm">{formatPercentage(comparison.eth_price_change_pct)}</span>}
                              onColumnClick={() => toggleColumn('ethChange')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-center transition-all duration-300 ${
                            visibleColumns.outperformance ? '' : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('outperformance') ? 'sticky z-10' : ''}`}
                          style={{
                            ...(pinnedColumns.includes('outperformance') ? { left: `${getStickyLeftPosition('outperformance')}px`, background: isDark ? '#09090b' : '#ffffff' } : {})
                          }}
                        >
                          {visibleColumns.outperformance ? (
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getOutperformanceBadge(comparison.outperformance)}`}>
                              {comparison.outperformance >= 0 ? '+' : ''}{comparison.outperformance.toFixed(1)}%
                            </span>
                          ) : (
                            <ColumnTooltip
                              content={
                                <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getOutperformanceBadge(comparison.outperformance)}`}>
                                  {comparison.outperformance >= 0 ? '+' : ''}{comparison.outperformance.toFixed(1)}%
                                </span>
                              }
                              onColumnClick={() => toggleColumn('outperformance')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-center transition-all duration-300 ${
                            visibleColumns.correlation ? '' : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('correlation') ? 'sticky z-10' : ''}`}
                          style={{
                            ...(pinnedColumns.includes('correlation') ? { left: `${getStickyLeftPosition('correlation')}px`, background: isDark ? '#09090b' : '#ffffff' } : {})
                          }}
                        >
                          {visibleColumns.correlation ? (
                            <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{comparison.correlation_with_eth.toFixed(2)}</span>
                          ) : (
                            <ColumnTooltip
                              content={
                                <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{comparison.correlation_with_eth.toFixed(2)}</span>
                              }
                              onColumnClick={() => toggleColumn('correlation')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-center transition-all duration-300 ${
                            visibleColumns.volatility ? '' : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('volatility') ? 'sticky z-10' : ''}`}
                          style={{
                            ...(pinnedColumns.includes('volatility') ? { left: `${getStickyLeftPosition('volatility')}px`, background: isDark ? '#09090b' : '#ffffff' } : {})
                          }}
                        >
                          {visibleColumns.volatility ? (
                            <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{comparison.volatility.toFixed(2)}%</span>
                          ) : (
                            <ColumnTooltip
                              content={
                                <span className={`text-sm ${isDark ? 'text-white' : 'text-gray-900'}`}>{comparison.volatility.toFixed(2)}%</span>
                              }
                              onColumnClick={() => toggleColumn('volatility')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                        <td
                          className={`px-3 py-2 text-center transition-all duration-300 ${
                            visibleColumns.marketCap ? '' : 'w-8 max-w-8'
                          } ${pinnedColumns.includes('marketCap') ? 'sticky z-10' : ''}`}
                          style={{
                            ...(pinnedColumns.includes('marketCap') ? { left: `${getStickyLeftPosition('marketCap')}px`, background: isDark ? '#09090b' : '#ffffff' } : {})
                          }}
                        >
                          {visibleColumns.marketCap ? (
                            <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-900'}`}>{formatNumber(comparison.market_cap)}</span>
                          ) : (
                            <ColumnTooltip
                              content={
                                <span className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-900'}`}>{formatNumber(comparison.market_cap)}</span>
                              }
                              onColumnClick={() => toggleColumn('marketCap')}
                            >
                              <div className="text-center text-gray-500 text-xs">•</div>
                            </ColumnTooltip>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* No Data State */}
            {data?.comparisons && data.comparisons.length === 0 && !loading && (
              <div className="px-6">
                <div className="text-center py-12">
                <div className={`text-5xl mb-4 ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>Ξ</div>
                <h3 className={`mt-2 text-sm font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>No comparison data found</h3>
                <p className={`mt-1 text-sm ${isDark ? 'text-gray-500' : 'text-gray-500'}`}>
                  No data available for the selected timeframe.
                </p>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Toast notification for pin limit */}
      {showPinLimitToast && (
        <div
          className={`fixed bottom-4 right-4 px-4 py-3 rounded-lg shadow-lg z-50 ${
            isDark
              ? 'bg-red-500/90 text-white border border-red-400'
              : 'bg-red-100 text-red-900 border border-red-300'
          }`}
          role="alert"
        >
          Limit reached (6). Unpin another column to continue.
        </div>
      )}
    </div>
  );
};

export default PriceComparisonETH;
